'use client';

import type React from 'react';
import { useMemo, useRef, useState } from 'react';
import { isNil } from 'es-toolkit';
import { BookmarkIcon, RefreshCwIcon } from 'lucide-react';

import type { SaveViewDialogLabels } from '../../../patterns/saved-views';
import type { Column, DataTableFilterActions, FiltersState, FilterStrategy } from '../core/types';
import type { Locale } from '../lib/i18n';
import type { ExtraItem } from './filter-selector';
import { SaveViewDialog } from '../../../patterns/saved-views';
import { FilterSelector } from './filter-selector';

type SavedView = {
  readonly id: string;
  readonly name: string;
};

type SavedViewsLabels = {
  readonly updateView: string;
  readonly selectView: string;
  readonly saveAsNewView: string;
  readonly saveAsView: string;
  readonly limitReached: string;
};

const DEFAULT_SAVED_VIEWS_LABELS: SavedViewsLabels = {
  updateView: 'Update view',
  selectView: 'Select a view',
  saveAsNewView: 'Save as new view',
  saveAsView: 'Save as view',
  limitReached: 'Limit reached (20 saved views)',
};

type SavedViewsConfig = {
  readonly atLimit: boolean;
  readonly activeViewName: string | null;
  readonly isModified: boolean;
  readonly hasFilters: boolean;
  readonly existingNames: string[];
  readonly savedViews: SavedView[];
  readonly onSave: (name: string) => void;
  readonly onUpdate: (id: string) => void;
  readonly labels?: Partial<SavedViewsLabels>;
  /**
   * Override copy for the "Save filter as view" dialog (title, placeholder,
   * error strings). Useful when the table's domain isn't tasks — the default
   * `e.g. My open tasks` placeholder leaks task-specific language onto every
   * other consumer otherwise.
   */
  readonly saveViewDialogLabels?: Partial<SaveViewDialogLabels>;
};

type ResultCountConfig = {
  readonly value: number;
  readonly singularLabel: string;
  readonly pluralLabel: string;
  readonly isLoading?: boolean;
};

interface DataTableFilterProps<TData> {
  columns: Column<TData>[];
  filters: FiltersState;
  actions: DataTableFilterActions;
  strategy: FilterStrategy;
  locale?: Locale;
  operatorDisabled?: boolean;
  filterValueVariant?: 'compact' | 'full';
  showClearFilterButton?: boolean;
  searchableColumnIds?: string[];
  footer?: React.ReactNode | ((close: () => void) => React.ReactNode);
  extraItems?: ExtraItem[];
  savedViews?: SavedViewsConfig;
  resultCount?: ResultCountConfig;
  /** `sm` for toolbars built on 32px controls; the trigger and its clear button follow. */
  triggerSize?: 'default' | 'sm';
}

export function DataTableFilter<TData>({
  columns,
  filters,
  actions,
  strategy,
  locale = 'en',
  operatorDisabled: _operatorDisabled = false,
  filterValueVariant: _filterValueVariant = 'full',
  showClearFilterButton: _showClearFilterButton = true,
  searchableColumnIds,
  footer,
  extraItems,
  savedViews,
  resultCount,
  triggerSize,
}: DataTableFilterProps<TData>) {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const closePopoverRef = useRef<(() => void) | null>(null);

  const labels = useMemo(
    () => ({
      ...DEFAULT_SAVED_VIEWS_LABELS,
      ...(savedViews?.labels ?? {}),
    }),
    [savedViews?.labels],
  );

  const hasActiveModifiedPreset =
    !isNil(savedViews) && savedViews.activeViewName !== null && savedViews.isModified && savedViews.hasFilters;

  const hasExactMatchPreset = !isNil(savedViews) && savedViews.activeViewName !== null && !savedViews.isModified;

  const showSaveButtons =
    !isNil(savedViews) && (hasActiveModifiedPreset || (savedViews.hasFilters && !hasExactMatchPreset));

  const savedViewsExtraItems = useMemo<ExtraItem[]>(() => {
    if (!savedViews || !hasActiveModifiedPreset) return [];
    return [
      {
        id: 'update-view',
        label: labels.updateView,
        icon: <RefreshCwIcon size={14} className="shrink-0" />,
        content: (
          <div className="p-1">
            <p className="px-2 py-1 text-xs font-medium text-[var(--muted-foreground)]">{labels.selectView}</p>
            {savedViews.savedViews.map((view) => (
              <button
                key={view.id}
                type="button"
                className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-[var(--component-hover)] focus:bg-[var(--component-hover)] focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => {
                  savedViews.onUpdate(view.id);
                  closePopoverRef.current?.();
                }}
              >
                <span className="truncate">{view.name}</span>
              </button>
            ))}
          </div>
        ),
      },
    ];
  }, [savedViews, hasActiveModifiedPreset, labels.updateView, labels.selectView]);

  const savedViewsFooter =
    showSaveButtons && !isNil(savedViews)
      ? (closePopover: () => void) => {
          closePopoverRef.current = closePopover;
          return savedViews.atLimit && savedViews.hasFilters ? (
            <p className="px-2 py-1.5 text-xs text-[var(--muted-foreground)]">{labels.limitReached}</p>
          ) : hasActiveModifiedPreset ? (
            <div className="p-1">
              {savedViews.hasFilters && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-[var(--component-hover)] focus:bg-[var(--component-hover)] focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => setSaveDialogOpen(true)}
                >
                  <BookmarkIcon size={14} className="shrink-0" />
                  {labels.saveAsNewView}
                </button>
              )}
            </div>
          ) : (
            <div className="p-1">
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-[var(--component-hover)] focus:bg-[var(--component-hover)] focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setSaveDialogOpen(true)}
              >
                <BookmarkIcon size={14} className="shrink-0" />
                {labels.saveAsView}
              </button>
            </div>
          );
        }
      : undefined;

  const mergedExtraItems = useMemo(
    () => [...(extraItems ?? []), ...savedViewsExtraItems],
    [extraItems, savedViewsExtraItems],
  );

  const mergedFooter =
    !isNil(footer) && savedViewsFooter !== undefined
      ? (closePopover: () => void) => (
          <>
            {typeof footer === 'function' ? footer(closePopover) : footer}
            {savedViewsFooter(closePopover)}
          </>
        )
      : (footer ?? savedViewsFooter);

  const resultCountLabel =
    !isNil(resultCount) && !resultCount.isLoading
      ? `${resultCount.value.toLocaleString()} ${resultCount.value === 1 ? resultCount.singularLabel : resultCount.pluralLabel}`
      : undefined;

  return (
    <>
      <FilterSelector
        columns={columns}
        filters={filters}
        actions={actions}
        strategy={strategy}
        locale={locale}
        searchableColumnIds={searchableColumnIds}
        footer={mergedFooter}
        extraItems={mergedExtraItems}
        resultCountLabel={resultCountLabel}
        triggerSize={triggerSize}
      />
      {!isNil(savedViews) && (
        <SaveViewDialog
          open={saveDialogOpen}
          onOpenChange={setSaveDialogOpen}
          onSubmit={(name) => {
            savedViews.onSave(name);
            closePopoverRef.current?.();
          }}
          existingNames={savedViews.existingNames}
          labels={savedViews.saveViewDialogLabels}
        />
      )}
    </>
  );
}

export type { ResultCountConfig, SavedView, SavedViewsConfig, SavedViewsLabels };
